import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, AuthSettings, LoginRequest, AcceptInviteRequest, AuthResponse, ForgotPasswordRequest, ResetPasswordRequest } from '../lib/types';
import { apiUrl } from '../lib/consts';

interface AuthContextType {
  user: User | null;
  token: string | null;
  authSettings: AuthSettings | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  acceptInvite: (data: AcceptInviteRequest) => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  refreshUser: () => Promise<void>;
  authFetch: (path: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'navaris_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [authSettings, setAuthSettings] = useState<AuthSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuthSettings();
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken) {
      setToken(storedToken);
      validateToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchAuthSettings = async () => {
    try {
      const response = await fetch(`${apiUrl}/auth/settings`);
      if (response.ok) {
        const settings = await response.json();
        setAuthSettings(settings);
      }
    } catch (error) {
      console.error('Failed to fetch auth settings:', error);
    }
  };

  const validateToken = async (tokenToValidate: string) => {
    try {
      const response = await fetch(`${apiUrl}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToValidate}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const parseErrorResponse = async (response: Response, fallbackMessage: string): Promise<string> => {
    const text = await response.text();
    try {
      const json = JSON.parse(text);
      return json.detail || fallbackMessage;
    } catch {
      return text || fallbackMessage;
    }
  };

  const login = async (credentials: LoginRequest) => {
    const response = await fetch(`${apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorMessage = await parseErrorResponse(response, 'Login failed');
      throw new Error(errorMessage);
    }

    const data: AuthResponse = await response.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
  };

  const acceptInvite = async (inviteData: AcceptInviteRequest) => {
    const response = await fetch(`${apiUrl}/users/accept-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inviteData),
    });

    if (!response.ok) {
      const errorMessage = await parseErrorResponse(response, 'Failed to accept invite');
      throw new Error(errorMessage);
    }

    const data: AuthResponse = await response.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
  };

  const forgotPassword = async (data: ForgotPasswordRequest) => {
    const response = await fetch(`${apiUrl}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorMessage = await parseErrorResponse(response, 'Failed to request password reset');
      throw new Error(errorMessage);
    }
  };

  const resetPassword = async (data: ResetPasswordRequest) => {
    const response = await fetch(`${apiUrl}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorMessage = await parseErrorResponse(response, 'Failed to reset password');
      throw new Error(errorMessage);
    }

    const result: AuthResponse = await response.json();
    localStorage.setItem(TOKEN_KEY, result.token);
    setToken(result.token);
    setUser(result.user);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await validateToken(token);
    }
  };

  const isAuthenticated = !!user && !!token;
  const isAdmin = user?.roles?.includes('administrator') ?? false;

  // Stable for the life of a token: pages load their data in effects that depend on it, so it
  // changes only when the token does, which is when a page should ask again.
  const authFetch = useCallback(
    (path: string, options: RequestInit = {}): Promise<Response> => {
      const headers = new Headers(options.headers);
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return fetch(`${apiUrl}${path}`, { ...options, headers });
    },
    [token],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        authSettings,
        loading,
        login,
        acceptInvite,
        forgotPassword,
        resetPassword,
        logout,
        isAuthenticated,
        isAdmin,
        refreshUser,
        authFetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
